/**
 * **The styles the COMBAT.app board still draws terrain overlays from** (`terrain-legibility` D7).
 *
 * Authored terrain is painted as tile art since `terrain-tile-art` (`decisions/0184`, `0192`): each
 * tile's look lives in `terrainArt/tileArt.ts`, and the per-mark holo treatments this module used to
 * hold (cover borders, hazard rims and glyphs, the pit well, the stipple, the glow walls, the preview
 * fills) went with the marks in 5.3. What stays is what is still drawn over the art: the armed-attack
 * cover hatch and the area-effect cloud, plus the render-class enumerations the folds in
 * `combatCells.ts` produce.
 *
 * Pure data: no Pixi, no domain. `cli/src/mapImage.ts` keeps its own PNG palette on purpose
 * (`terrain-legibility` D8).
 *
 * @module
 */

/** A cell's render class, as `combatCells.ts`'s `cellChannels` allocates it across the floor, cover
 *  and sight channels. The board no longer draws one mark per class — the tile art carries every
 *  property — but the fold still answers "what does this cell do" for the hover pane and authoring. */
export type CellVisual = "block" | "hard" | "soft" | "pit" | "sight" | "difficult";

/**
 * An edge wall's render class. `full` blocks sight; the other three are the flyable, sight-passing
 * **low** wall, split by the cover grade it grants (`EdgeTerrain.cover`): `low` none, `low-soft`
 * soft, `low-hard` hard. The terrain painter draws each in the rim language, the low walls keeping a
 * dash grammar for their grade (dotted none, dashed soft, solid hard).
 */
export type EdgeVisual = "full" | "low" | "low-soft" | "low-hard";

/** Every {@link EdgeVisual}, in the order the legend lists them: the full wall, then the low walls from ungraded to hard. */
export const EDGE_VISUALS = ["full", "low", "low-soft", "low-hard"] as const satisfies readonly EdgeVisual[];

// ── The armed-attack cover underlay ───────────────────────────────────────────────────────────────

/**
 * A 45° diagonal hatch over a faint wash. It used to be how authored cover drew, and hard and soft
 * differed only in pitch and alpha, which converge at zoom-out. It survives for one consumer:
 * {@link ARMED_COVER_HATCH}, which answers a different question ("is this cell in cover relative to
 * MY armed shot"), so it keeps a mark the ground's own art doesn't use.
 */
export type HatchStyle = {
  kind: "hatch";
  wash: string;
  hatch: string;
  hatchWidth: number;
  /** Pitch between adjacent diagonals, in world px. Smaller = denser = harder cover. */
  gap: number;
  alpha: number;
};

/**
 * The per-cell hatch the board paints inside the dashed attack-range boundary **while an attack is armed**, at the
 * cover degree `armedCellSightlines` resolved for that cell against this attacker and this weapon.
 *
 * **This is the only place the 45° hatch survives, and the split is deliberate.** The tile art says
 * "this cell grants cover to whoever shelters here" (prop size and shadow length, ruling 5) and is a
 * property of the ground. The hatch says
 * "relative to MY armed shot, the target on this cell is in cover" — a resolved, attacker- and
 * weapon-specific fact that folds four carriers, the occupancy floor and the hard-cover adjacency
 * rule, and that vanishes the moment the attack is disarmed. Two questions, two marks.
 *
 * Retuned for the textured ground (`terrain-tile-art` 5.1, ruling 6): a light hatch over a slight
 * darkening, each line on a dark under-line the board draws, so it reads on grass and rock alike.
 * The old cyan at 0.16 / 0.08 vanished on the new ground. Eyeball-tuned; part of the Phase 6 eyeball item.
 */
export const ARMED_COVER_HATCH = {
  hard: { kind: "hatch", wash: "rgba(6, 8, 12, 0.18)", hatch: "#e8fbff", hatchWidth: 1.5, gap: 5, alpha: 0.55 },
  soft: { kind: "hatch", wash: "rgba(6, 8, 12, 0.10)", hatch: "#e8fbff", hatchWidth: 1.5, gap: 9, alpha: 0.35 },
} as const satisfies Record<Extract<CellVisual, "hard" | "soft">, HatchStyle>;

// ── Area effects ──────────────────────────────────────────────────────────────────────────────────

/** A live area-effect cloud (standing smoke and friends) — deliberately NOT tile art and NOT an
 *  object token. A hazy off-white/grey wash with a blurred edge, so it reads as an atmospheric layer
 *  drifting over the map rather than painted ground; the board adds an exact dashed edge over the
 *  blur (`terrain-tile-art` 5.1). The breathing alpha drift is animation, not palette, and stays with
 *  the renderer. */
export type AreaEffectStyle = { fill: string; blur: number };

export const AREA_EFFECT_STYLE = { fill: "rgba(220, 224, 228, 0.28)", blur: 7 } as const satisfies AreaEffectStyle;

/**
 * A shield dome (`grants: "outside_attack_difficulty"`, the ENCLAVE-Pattern Support Shield) is not
 * smoke: it gives no cover, and reading it as a cloud would misstate the rule. A pale cyan wash with
 * a barely softened edge, so it reads as a hard energy shell.
 */
export const SHIELD_DOME_STYLE = { fill: "rgba(120, 220, 255, 0.18)", blur: 1.5 } as const satisfies AreaEffectStyle;

/** The fill an area draws with, by its `AreaEffect.grants`. Every grant but the dome keeps the cloud. */
export function areaEffectStyle(grants: string): AreaEffectStyle {
  return grants === "outside_attack_difficulty" ? SHIELD_DOME_STYLE : AREA_EFFECT_STYLE;
}
